import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { injectBrandingCSS } from "../config/branding";

type Theme = "light" | "dark" | "crimson";

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Brand-specific localStorage key
const THEME_STORAGE_KEY = "limitless-ladies-theme";

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Check localStorage first, default to dark
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;
      if (stored === "light" || stored === "dark" || stored === "crimson") {
        return stored;
      }
    }
    return "light";
  });

  // Inject branding CSS variables on mount
  useEffect(() => {
    injectBrandingCSS();
  }, []);

  useEffect(() => {
    // Apply theme class to document
    const root = document.documentElement;
    root.classList.remove("dark", "dark-bg", "crimson");
    // Always clear any JS-injected background
    document.body.style.backgroundImage = "";
    document.body.style.backgroundColor = "";
    if (theme === "dark") {
      root.classList.add("dark");
    } else if (theme === "crimson") {
      root.classList.add("crimson");
    }
    // Persist to localStorage
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    // Re-inject branding CSS to update background colors based on theme
    injectBrandingCSS();
  }, [theme]);

  const toggleTheme = () => {
    setThemeState((prev) => {
      if (prev === "light") return "dark";
      if (prev === "dark") return "crimson";
      return "light";
    });
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
